import bind from 'bind-decorator';
import { LocationDescriptor } from 'history';
import update from 'immutability-helper';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Redirect } from 'react-router-dom';
import { MessageDescriptor } from 'react-intl';

import { getUsername } from 'auth/selectors';
import { authLocalStorageManager } from 'auth/utils';
import { UsersApi } from 'global/api';
import { ROUTES } from 'global/constants';
import { RootState } from 'global/state';
import { Profile, ProfileOnboardingStatusEnum, ProfileProfileVisibilityEnum, ProfileRoleEnum } from 'labxchange-client';
import { UI_IS_SM } from 'ui/breakpoints';
import { CalendarDate } from 'ui/components';
import { showErrorMessage } from 'ui/components/GlobalMessageReporter/dispatch';
import { StandardPageLayout } from 'ui/components/StandardPageLayout';
import * as UserActions from 'user/actions';
import { WrappedMessage } from 'utils';
import messages from '../displayMessages';
import { OnboardingDetails } from './OnboardingDetails';

interface StepsProps {
    current: number;
    total: number;
}

class Steps extends React.PureComponent<StepsProps> {
    public render() {
        const steps = [];
        for (let i = 1; i <= this.props.total; i++) {
            const active = i === this.props.current;

            steps.push(
                <div
                    key={i}
                    className={active ? 'step-active' : 'step-inactive'}
                    aria-hidden='true'
                >
                    <div className='counter'>{active ? i : ''}</div>
                </div>,
            );
        }
        return (
            <div className='steps'>
                <div className='line' aria-hidden='true'></div>
                {steps}
                <span className='sr-only'>
                    <WrappedMessage
                        message={messages.stepProgressText}
                        values={{step: this.props.current, numSteps: this.props.total}}/>
                </span>
            </div>
        );
    }
}

interface RoleProps {
    name: ProfileRoleEnum;
    title: MessageDescriptor;
    description: MessageDescriptor;
    buttonLabel: MessageDescriptor;
    onSelect: (role?: ProfileRoleEnum) => void;
    onActive: (activeRole: ProfileRoleEnum) => void;
    active: boolean;
}

class Role extends React.PureComponent<RoleProps> {
    public render() {
        return (
            <div
                onClick={this.selectRoleWithMouse}
                tabIndex={0}
                onKeyPress={this.selectRoleWithKeyboard}
                className={`role ${this.props.active ? 'role-active' : ''}`}
                role='button'>
                    <div className={`hexagon hexagon-${this.props.name}`}></div>
                    <h2><WrappedMessage message={this.props.title}/></h2>
                    <div className='description'>
                        <WrappedMessage message={this.props.description}/>
                    </div>
                    {this.props.active ?
                    <div className='extended-form small'>
                        <button className='btn' onClick={(e) => this.onConfirm(e, this.props.name)}>
                            <WrappedMessage message={this.props.buttonLabel}/>
                        </button>
                    </div>
                    : null
                    }
            </div>
        );
    }

    @bind private onConfirm(event: React.SyntheticEvent, role?: ProfileRoleEnum) {
        event.stopPropagation();
        this.props.onSelect(this.props.name);
    }

    @bind private selectRoleWithKeyboard(event: React.KeyboardEvent<HTMLInputElement>) {
        if (!this.props.active) {
            this.props.onActive(this.props.name);
        }
    }

    @bind private selectRoleWithMouse(event: React.MouseEvent<HTMLDivElement>) {
        if (!this.props.active) {
            this.props.onActive(this.props.name);
        }
    }
}

enum OnboardingSteps {
    None = 0,
    RolePicker,
    Profile,
    Completed,
}

interface OnboardingPageProps extends RouteComponentProps<{}> {}

interface ReduxStateProps {
    loggedInUsername: string;
}

interface ReduxActionProps {
    reloadAvatarImage: typeof UserActions.reloadOwnAvatarImage;
}

interface OnboardingPageState {
    redirectTo?: LocationDescriptor;
    step: OnboardingSteps;
    profile?: Profile;
    activeRole?: ProfileRoleEnum;
    isMobileView: boolean;
}

type Props = OnboardingPageProps & ReduxStateProps & ReduxActionProps;

class OnboardingPageInternal extends React.PureComponent<Props, OnboardingPageState> {
    private mediaQuery = UI_IS_SM;

    constructor(props: Props) {
        super(props);
        this.state = {
            step: OnboardingSteps.None,
            isMobileView: false,
        };
    }

    public async componentDidUpdate(prevProps: Props) {
        if (this.props.loggedInUsername !== prevProps.loggedInUsername) {
            await this.initializePage();
        }
    }

    public componentWillUnmount() {
        this.mediaQuery.removeListener(this.setIsMobileView);
    }

    public async componentDidMount() {
        this.setIsMobileView();
        this.mediaQuery.addListener(this.setIsMobileView);
        await this.initializePage();
    }

    public async initializePage() {

        // Try to retrieve user birthday from localStorage
        let birthdate = authLocalStorageManager.userBirthdate;

        // If a loggedInUsername is present, fetch user profile
        if (this.props.loggedInUsername) {
            // Fetch user profile
            await this.fetchUser();
        }

        // If we failed to fetch user profile or loggedInUsername
        // is not present, redirect to sign in.
        // User should be logged in to do onboarding
        if (!this.props.loggedInUsername || this.state.profile === undefined) {
            authLocalStorageManager.onboardingStatus = null;
            this.setState({ redirectTo: ROUTES.General.SIGN_IN });
            return;
        }

        // If userBirthdate is not in localStorage, look at user profile
        if (birthdate === null) {
            // If user profile doesn't have birthday we remove
            // localStorage 'userBirthdate' key to tell onboarder
            // middleware to redirect to sign up view (for birthday prompt)
            // and we redirect user to sign up view.
            if (this.state.profile === undefined ||
                this.state.profile.dateOfBirth === undefined) {
                    authLocalStorageManager.onboardingStatus = null;
                    authLocalStorageManager.userBirthdate = null;
                    this.setState({redirectTo: ROUTES.General.SIGN_UP});
                    return;
            } else {
                birthdate = CalendarDate.fromJsDate(this.state.profile.dateOfBirth);
            }
        }

        // If user onboarding status is already completed for unknown reason
        // it will be redirect to explore page.
        if (this.state.profile.onboardingStatus === ProfileOnboardingStatusEnum.Completed) {
            this.setState({step: OnboardingSteps.Completed});
            return;
        }

        // We need to determine user age based on its birthday
        // to show available roles.
        const isBelow18 = birthdate.ageInYears <= 18;

        // Prepare user profile data to update
        const updateData = {
            date_of_birth: birthdate.isoString,
        } as any;

        if (isBelow18) {
            // If user is below 18, it can only be a learner
            updateData.role = ProfileRoleEnum.Learner;
            // DMs are disabled by default for users under 18, but enabled by
            // default otherwise.
            updateData.direct_messages_enabled = false;
        } else {
            updateData.direct_messages_enabled = true;
            updateData.profile_visibility = ProfileProfileVisibilityEnum.LabxchangeUsers;
            updateData.include_in_search_results = true;
        }

        try {
            // Update user profile based on prepared data
            await UsersApi.profilesPartialUpdate({
                id: this.props.loggedInUsername, data: updateData,
            });
            // Retrieve user profile (just to be safe)
            const user = await UsersApi.profilesRead({id: this.props.loggedInUsername});
            // Update user profile stored in current component state
            this.setState({
                profile: update(
                    this.state.profile,
                    {
                        dateOfBirth: {$set: user.dateOfBirth},
                        role: {$set: user.role},
                    },
                ),
                // If user is below 18, we don't show role picker since it can only be learner
                step: isBelow18 ? OnboardingSteps.Profile : OnboardingSteps.RolePicker,
            });
            // Clean localStorage
            authLocalStorageManager.userBirthdate = (
                user.dateOfBirth ? CalendarDate.fromJsDate(user.dateOfBirth) : null
            );
        } catch (err) {
            showErrorMessage(
                <WrappedMessage message={messages.errorMessageUnableToUpdateUserProfile}/>,
                {
                    exception: err,
                    details: 'Unable to update user birthdate',
                },
            );
        }

        if (this.state.profile.onboardingStatus === ProfileOnboardingStatusEnum.StepTwo) {
            this.setState({step: OnboardingSteps.Profile});
            return;
        }
    }
    public getComponent() {
        if (this.state.profile === undefined) { return; }
        switch (this.state.step) {
            case OnboardingSteps.RolePicker: {
                return (
                    <div className='onboarding-content'>
                        <h1><WrappedMessage message={messages.selectRoleTitle}/></h1>
                        <p className='description'>
                            We want to make your LabXchange experience fun and useful. If you are here to learn,
                            explore the library, and ask questions, we recommend <strong>I am a learner</strong>.
                            If you want to create classes for small groups of learners, we
                            recommend<strong> I am an educator</strong>. You can switch roles at any time.
                            {this.state.isMobileView ?
                            <>
                                If you want to skip this step for now, we'll select
                                <strong> I am a learner</strong> for you.
                            </>
                            : null}
                        </p>
                        <div className='role-cards'>
                            <Role
                                name={ProfileRoleEnum.Learner}
                                title={messages.learnerTitleRole}
                                description={messages.learnerDescriptionRole}
                                buttonLabel={messages.learnerRegisterAsButtonLabel}
                                onActive={this.onActiveRole}
                                onSelect={this.onSelectRole}
                                active={this.state.activeRole === ProfileRoleEnum.Learner}
                            />
                            <Role
                                name={ProfileRoleEnum.Educator}
                                title={messages.educatorTitleRole}
                                description={messages.educatorDescriptionRole}
                                buttonLabel={messages.educatorRegisterAsButtonLabel}
                                onActive={this.onActiveRole}
                                onSelect={this.onSelectRole}
                                active={this.state.activeRole === ProfileRoleEnum.Educator}
                            />
                        </div>
                    </div>
                );
            }
            case OnboardingSteps.Profile: {
                return <OnboardingDetails profile={this.state.profile} onAvatarUpdate={this.userAvatarChanged} />;
            }
            case OnboardingSteps.Completed: {
                return (<Redirect to={ROUTES.Explore.HOME} push={true}/>);
            }
        }
        return (null);
    }

    public render() {
        if (this.state.redirectTo) {
            return <Redirect to={this.state.redirectTo} push={true}/>;
        }

        return (
            <StandardPageLayout containerClassName='container-fluid'>
                <div className='onboarding'>
                    {this.state.step > 0 && <div className='steps-and-skip'>
                        <Steps current={this.state.step + 1} total={3}/>
                        {this.state.step === OnboardingSteps.RolePicker ?
                        <button className='btn btn-outline-primary' onClick={() => this.onSelectRole()}>
                            <WrappedMessage message={messages.skipRoleRegistrationButton}/>
                        </button>
                        : null}
                    </div>}
                    {this.getComponent()}
                </div>
            </StandardPageLayout>
        );
    }

    // Event called from step 3 if the user's avatar has changed.
    private userAvatarChanged = async () => {
        // Reload the user's profile data, including the new value of
        // publicAvatarUrl, which gets passed back to <OnboardingDetails>
        this.fetchUser();
        // And tell every other part of the app that it needs to reload the avatar
        this.props.reloadAvatarImage();
    }

    private fetchUser = async () => {
        try {
            const profile = await UsersApi.profilesRead({id: this.props.loggedInUsername});
            this.setState({profile});
            authLocalStorageManager.onboardingStatus = profile.onboardingStatus || ProfileOnboardingStatusEnum.StepOne;
        } catch (err) {
            return;
        }
    }

    @bind private onActiveRole(activeRole: ProfileRoleEnum) {
        this.setState({activeRole});
    }

    @bind private async onSelectRole(role?: ProfileRoleEnum) {
        if (this.state.profile === undefined) { return; }

        // If not role is passed, it means that user want to skip it
        if (role === undefined) {
            this.setState({step: OnboardingSteps.Profile});
            authLocalStorageManager.onboardingStatus = ProfileOnboardingStatusEnum.StepTwo;
            return;
        }

        try {
            const data = {
                role,
                onboarding_status: ProfileOnboardingStatusEnum.StepTwo,
            };
            await UsersApi.profilesPartialUpdate({
                id: this.props.loggedInUsername, data,
            });
            this.setState({
                step: OnboardingSteps.Profile,
                profile: update(
                    this.state.profile,
                    {role: {$set: role}},
                ),
            });

            authLocalStorageManager.onboardingStatus = ProfileOnboardingStatusEnum.StepTwo;
        } catch (err) {
            showErrorMessage(
                <WrappedMessage message={messages.errorMessageUnableToUpdateUserProfile}/>,
                {
                    exception: err,
                    details: 'Unable to update user role',
                },
            );
        }
    }

    @bind private setIsMobileView() {
        this.setState({isMobileView: this.mediaQuery.matches});
    }
}

export const OnboardingPage = connect<ReduxStateProps, ReduxActionProps, RootState>(
    (state: RootState) => ({
        loggedInUsername: getUsername(state),
    }), {
        reloadAvatarImage: UserActions.reloadOwnAvatarImage,
    },
)(OnboardingPageInternal);
